import { useLayoutEditorContext } from '../../../../hooks/context/useLayoutEditorContext';
import { GridInputValue } from '../../../../types/types';

// Handlers of the editor side panel sections in one place. Each section
// component calls the hook and picks the handlers it needs — they only wrap
// context actions, so every caller gets the same behavior.
export const useEditorPanelHandlers = () => {
  const {
    selectedIndex,
    updateObject,
    addObject,
    removeObject,
    patchCanvas,
    applyObjectDefault,
    applyCanvasDefault,
    openMatrixDialog,
    setFocusedObjectField,
  } = useLayoutEditorContext();

  // "Smazat" button of the object section — removes the selected object (the
  // next one in order gets selected by removeObject).
  const handleDeleteObject = (): void => {
    if (selectedIndex === null) {
      return;
    }

    removeObject(selectedIndex);
  };

  // "Přidat objekt" button of the canvas section — same as double-clicking the
  // canvas, just without a pointer position: the object lands at the origin
  // (snapped/clamped by addObject) and gets selected.
  const handleAddObject = (): void => {
    addObject(0, 0);
  };

  // "Generovat matici" button — opens the matrix dialog. Its state lives in
  // the layout editor context, so it survives the fullscreen reference route.
  const handleOpenMatrixModal = (): void => {
    openMatrixDialog();
  };

  // User edit of an object form field — written into the selected object
  // (definition item names match the API object keys 1:1).
  const handleObjectFieldChange = (
    name: string,
    value: GridInputValue
  ): void => {
    if (selectedIndex === null) {
      return;
    }

    updateObject(selectedIndex, { [name]: value });
  };

  // User edit of a canvas form field — merged into the canvas settings
  // (patchCanvas sanitizes the value and clears its pending save error).
  const handleCanvasFieldChange = (
    name: string,
    value: GridInputValue
  ): void => {
    patchCanvas({ [name]: value });
  };

  // Programmatic default of an object form field (radioBtn/select first option
  // for an undefined value) — written into the state and the baseline, so it
  // doesn't count as a change.
  const handleObjectFieldDefault = (
    name: string,
    value: GridInputValue
  ): void => {
    if (selectedIndex === null) {
      return;
    }

    applyObjectDefault(selectedIndex, { [name]: value });
  };

  const handleObjectFieldFocus = (name: string): void => {
    setFocusedObjectField(name.toLowerCase());
  };

  const handleObjectFieldBlur = (): void => {
    setFocusedObjectField(null);
  };

  // Canvas counterpart of handleObjectFieldDefault — default into state +
  // baseline, never a change.
  const handleCanvasFieldDefault = (
    name: string,
    value: GridInputValue
  ): void => {
    applyCanvasDefault({ [name]: value });
  };

  return {
    handleDeleteObject,
    handleAddObject,
    handleOpenMatrixModal,
    handleObjectFieldChange,
    handleCanvasFieldChange,
    handleObjectFieldDefault,
    handleCanvasFieldDefault,
    handleObjectFieldFocus,
    handleObjectFieldBlur,
  };
};
